import {
  Button,
  Drawer,
  Menu,
  MenuItem,
  NonIdealState,
  OverlayToaster,
  Position,
} from "@blueprintjs/core";
import type { IconName } from "@blueprintjs/icons";
import { Select } from "@blueprintjs/select";
import { css } from "@emotion/core";
import * as React from "react";
import { useLocation, Outlet, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ApiAdmin from "../../api/admin";
import { useAuthState } from "../../context/auth";
import { isFybrAdminUser } from "./adminRoles";
import { useInternalCloudAdminGate } from "./useInternalCloudAdminGate";
import { useOrganizationAdminGate } from "./useOrganizationAdminGate";
import { buildAdminNavItems } from "./buildAdminNavItems";

const internalNavItems: Array<{
  path: string;
  icon: IconName;
  label: string;
}> = [
  { path: "/internal/organizations", icon: "office", label: "Organizations" },
  { path: "/internal/order-templates", icon: "list", label: "Order Templates" },
  {
    path: "/internal/chart-properties",
    icon: "chart",
    label: "Chart Properties",
  },
];

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { organization_id } = useParams<{ organization_id: string }>();

  const toaster = React.useRef<OverlayToaster>(null);
  const { currentUser } = useAuthState();
  const internalCloudAdminGate = useInternalCloudAdminGate();
  const organizationAdminGate = useOrganizationAdminGate();

  const isInternalPath = location.pathname.includes("/internal");

  const { data: organizationsData } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => ApiAdmin.getAdminOrganizations(),
    enabled: !isInternalPath && organizationAdminGate === "allowed",
  });

  const filteredOrganizations = React.useMemo(() => {
    if (!organizationsData?.organizations) return [];

    if (currentUser?.organization_role === "admin") {
      return organizationsData.organizations.filter(
        (org) => org.id === currentUser.organization_id
      );
    }

    return organizationsData.organizations;
  }, [organizationsData?.organizations, currentUser]);

  const [selectedOrganizationId, setSelectedOrganizationId] = React.useState<
    number | null
  >(organization_id ? Number(organization_id) : null);

  React.useEffect(() => {
    if (organization_id) {
      setSelectedOrganizationId(Number(organization_id));
    }
  }, [organization_id]);

  const isFybrAdmin = isFybrAdminUser(currentUser);

  const adminNavItems = React.useMemo(() => {
    if (!selectedOrganizationId) return [];
    return buildAdminNavItems(selectedOrganizationId, !!isFybrAdmin);
  }, [selectedOrganizationId, isFybrAdmin]);

  if (isInternalPath && internalCloudAdminGate !== "allowed") return null;
  if (!isInternalPath && organizationAdminGate === "pending") return null;

  const handleOrganizationChange = (orgId: number) => {
    setSelectedOrganizationId(orgId);
    const subMenus = adminNavItems
      .map((i) => i.path.split("/").pop())
      .filter((s): s is string => Boolean(s));
    const current = location.pathname.split("/")[4] ?? "";
    const subMenu = subMenus.includes(current) ? current : "settings";
    navigate(`/admin/organizations/${orgId}/${subMenu}`);
  };

  return (
    <Drawer
      icon="panel-table"
      isOpen
      size="calc(100vh - 66px)"
      position="bottom"
      className="bp6-dark"
      title={`${
        location.pathname.includes("/internal")
          ? "FYBR Admin"
          : "Manage Organization"
      }`}
      canOutsideClickClose
      canEscapeKeyClose
      isCloseButtonShown
      onClose={() => navigate("..")}
    >
      <OverlayToaster position={Position.BOTTOM_RIGHT} ref={toaster} />
      <div
        css={css`
          flex: 1;
          min-height: 0;
          display: flex;
        `}
      >
        {!isInternalPath && organizationAdminGate === "denied" ? (
          <NonIdealState
            icon="lock"
            title="Not authorized"
            description="Organization admin access is required to manage this organization."
          />
        ) : (
          <>
            <nav
              css={css`
                width: 256px;
                background-color: #2f343c;
                height: 100%;
                padding: 0.25rem;
                border-right: 1px solid var(--neutral-6);
              `}
            >
              {isInternalPath ? (
                <Menu>
                  {internalNavItems.map((item) => (
                    <MenuItem
                      key={item.path}
                      icon={item.icon}
                      text={item.label}
                      active={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                      css={css`
                        margin-bottom: 0.25rem;
                      `}
                    />
                  ))}
                </Menu>
              ) : (
                <>
                  {filteredOrganizations && filteredOrganizations.length > 0 && (
                    <div
                      css={css`
                        padding: 0.25rem;
                        border-bottom: 1px solid var(--neutral-6);
                      `}
                    >
                      {filteredOrganizations.length === 1 ? (
                        <Button icon="office" fill alignText="left" disabled>
                          {filteredOrganizations[0].name}
                        </Button>
                      ) : (
                        <Select
                          items={filteredOrganizations}
                          itemRenderer={(org, { handleClick, modifiers }) => {
                            return (
                              <MenuItem
                                key={org.id}
                                text={org.name}
                                active={modifiers.active}
                                onClick={handleClick}
                              />
                            );
                          }}
                          activeItem={filteredOrganizations.find(
                            (org) => org.id === selectedOrganizationId
                          )}
                          itemPredicate={(query, org) =>
                            org.name.toLowerCase().includes(query.toLowerCase())
                          }
                          onItemSelect={(org) =>
                            handleOrganizationChange(org.id)
                          }
                          popoverProps={{
                            minimal: true,
                            matchTargetWidth: true,
                          }}
                        >
                          <Button
                            icon="office"
                            endIcon="chevron-down"
                            fill
                            alignText="left"
                          >
                            {filteredOrganizations.find(
                              (org) => org.id === selectedOrganizationId
                            )?.name || "Select Organization"}
                          </Button>
                        </Select>
                      )}
                    </div>
                  )}

                  <Menu>
                    {adminNavItems.map((item) => (
                      <MenuItem
                        key={item.path}
                        icon={item.icon}
                        text={item.label}
                        active={location.pathname === item.path}
                        onClick={() => navigate(item.path)}
                        css={css`
                          margin-bottom: 0.25rem;
                        `}
                      />
                    ))}
                  </Menu>
                </>
              )}
            </nav>
            <div
              css={css`
                flex: 1;
                overflow: hidden;
              `}
            >
              <Outlet context={{ toaster }} />
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
